"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import { Autoplay } from "swiper/modules"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Entrance reveal (once on viewport entry, ASH-1951) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setInView(true)
      return
    }
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      // fires when 15% is visible (no negative rootMargin) so a gallery that sits
      // at the bottom of a short page still reveals
      { threshold: 0.15 },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Icons ---

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      <path
        d={dir === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* two bars 8x25 with a 6px gap, per Figma (node 2174-16464) */
function PauseIcon() {
  return (
    <svg
      width="20"
      height="23"
      viewBox="0 0 22 25"
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <rect x="0" y="0" width="8" height="25" />
      <rect x="14" y="0" width="8" height="25" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg
      width="20"
      height="23"
      viewBox="0 0 22 25"
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <path d="M2 1.5v22l19-11L2 1.5z" />
    </svg>
  )
}

// --- Styled Components ---

const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: 40px 0;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 0;
  }
`

const Title = styled.h2`
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--comp-heading);
  text-align: center;
  margin: 0 20px 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
    margin-bottom: 40px;
  }
`

/* subtle fade-in of the gallery once it scrolls into view (ASH-1951) */
const FadeIn = styled.div<{ $in: boolean }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "24px")});
  transition: opacity 0.7s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const CarouselWrapper = styled.div`
  position: relative;

  .swiper {
    overflow: visible;
    padding: 0;
  }

  .swiper-slide {
    /* fixed gallery card size, uniform crop (ASH-1598). Design ratio ~414/367 */
    width: 246px;
    ${(props) => props.theme.breakpoints.up("md")} {
      width: 414px;
    }
  }
`

const Slide = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 414 / 367;
  border-radius: 8px;
  overflow: hidden;
`

const SlideImage = styled(Image)`
  object-fit: cover;
`

/* half-circle edge buttons; directional props (left/right, border-radius) are set
   inline so the emotion RTL plugin doesn't mirror them */
const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 58px;
  border: none;
  padding: 0;
  cursor: pointer;
  background-color: #878b9d;
  color: #fff;

  &:hover {
    opacity: 0.9;
  }
  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 81px;
    height: 98px;
  }
`

const PlayPauseBtn = styled.button`
  position: absolute;
  bottom: 20px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background-color: #fff;
  color: #020a0d;

  &:hover {
    opacity: 0.9;
  }
  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 50px;
    height: 50px;
  }
`

// --- Main Component ---

function Gallery1({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const { ref: sectionRef, inView } = useInViewOnce<HTMLElement>()
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  // "locked" = all images fit on screen with nothing to scroll (Swiper watchOverflow).
  // In that case we hide the arrows/play-pause and stop autoplay — the images just sit centered.
  const [locked, setLocked] = useState(false)

  const topTitle = extractProperty(data, "topTitle") as string | ""
  const images = (extractProperty(data, "images", true) as any[] | undefined) || []
  const slides = images.filter((img) => img?.url)

  // Do all the images fit on screen? (Swiper's own isLocked is unreliable with
  // centeredSlides, so measure the slides vs the container.) When they fit we hide
  // the arrows/play-pause and stop autoplay — nothing to scroll.
  const syncOverflow = (swiper: SwiperType) => {
    const gap = (swiper.params.spaceBetween as number) || 0
    const total =
      Array.from(swiper.slides).reduce((sum, el) => sum + (el as HTMLElement).offsetWidth, 0) +
      Math.max(0, swiper.slides.length - 1) * gap
    const fits = total <= swiper.width + 1
    setLocked(fits)
    // Slides are never centered on the active item — that left empty gaps at both
    // edges during the auto-transition (ASH-1950). centerInsufficientSlides still
    // centers the whole group when the few images all fit; on overflow the slides
    // left-align and fill the width edge to edge.
    if (fits) swiper.autoplay?.stop()
    else if (isPlaying) swiper.autoplay?.start()
  }

  const togglePlay = () => {
    if (!swiperRef) return
    if (isPlaying) swiperRef.autoplay?.stop()
    else swiperRef.autoplay?.start()
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.gallery)
    setIsPlaying(!isPlaying)
  }

  if (!topTitle && slides.length === 0) return null

  return (
    <Section ref={sectionRef}>
      <FadeIn $in={inView}>
        {topTitle && <Title>{topTitle}</Title>}
        {slides.length > 0 && (
          <CarouselWrapper>
            <Swiper
              modules={[Autoplay]}
              onSwiper={(swiper) => {
                setSwiperRef(swiper)
                syncOverflow(swiper)
              }}
              onResize={syncOverflow}
              dir={isRtl ? "rtl" : "ltr"}
              slidesPerView="auto"
              centerInsufficientSlides
              rewind
              spaceBetween={16}
              breakpoints={{ 768: { spaceBetween: 24 } }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
            >
              {slides.map((img, i) => (
                <SwiperSlide key={i}>
                  <Slide>
                    <SlideImage
                      src={getUmbracoMediaUrl(img.url)}
                      fill
                      alt={img.altText || ""}
                      sizes="(max-width: 768px) 246px, 414px"
                    />
                  </Slide>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Controls only when the images overflow (something to scroll); hidden when they all fit. */}
            {!locked && (
              <>
                {/* left edge = arrow-left; in RTL that advances (next), in LTR it's prev */}
                <ArrowBtn
                  style={{ left: 0, borderRadius: "0 50px 50px 0" }}
                  onClick={() => (isRtl ? swiperRef?.slideNext() : swiperRef?.slidePrev())}
                  aria-label={isRtl ? "התמונה הבאה" : "Previous image"}
                >
                  <Chevron dir="left" />
                </ArrowBtn>
                <ArrowBtn
                  style={{ right: 0, borderRadius: "50px 0 0 50px" }}
                  onClick={() => (isRtl ? swiperRef?.slidePrev() : swiperRef?.slideNext())}
                  aria-label={isRtl ? "התמונה הקודמת" : "Next image"}
                >
                  <Chevron dir="right" />
                </ArrowBtn>

                <PlayPauseBtn
                  style={{ left: 20 }}
                  onClick={togglePlay}
                  aria-label={
                    isPlaying
                      ? isRtl
                        ? "עצור תנועת הרכיב"
                        : "Stop animation"
                      : isRtl
                      ? "הפעל תנועה רכיב"
                      : "Play animation"
                  }
                >
                  {isPlaying ? <PauseIcon /> : <PlayIcon />}
                </PlayPauseBtn>
              </>
            )}
          </CarouselWrapper>
        )}
      </FadeIn>
    </Section>
  )
}

export default Gallery1
